import React from 'react';
import { connect } from 'react-redux';

import { Messages } from '../../../codegen/Localize';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { L10n } from '../../../ts/L10n/L10n';
import { DatasourceContentType, FlowContentType, WorkbookContentType } from '../../../ts/models/ContentTypes';
import { ExtractEncryptionState, IDataSourceDetailInfo } from '../../../ts/models/IDataSourceInfo';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { FlowAllowedActions } from '../../../ts/ServerApi/types/FlowAllowedActions';
import { IDatasourceActions } from '../../../ts/ServerApi/types/IDatasourceActions';
import { IWorkbookActions } from '../../../ts/ServerApi/types/IWorkbookActions';
import { CompositeSessionInfoSelectors, SessionServerSelectors, SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { extractMoveToTheWebEnabled } from '../ActionsMenu/EntityActions/IEntityActionHelpers';
import { ConnectedCancelExtractActionDropdown } from './CancelExtractActionDropdown';
import { ConnectedDataSourceActionDropdown, DataSourceActionDropdown } from './DataSourceActionDropdown';
import { SpaceHeaderTestIds } from './SpaceHeader';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';
import { ExtractEncryptionMode } from '../../../sites/SitePropertyTypes';

export function ExtractTasksMetadata(props: ExtractTasksMetadataProps): JSX.Element {
    const { itemInfo, availableActions, isEncryptionEnabled, siteEARMode, isWebExtractionEnabled} = props;
    const showDataStatus: boolean = itemInfo.contentType === DatasourceContentType.name;
    const pendingEncryption = ((itemInfo.hasExtracts || itemInfo.hasEncryptableExtracts) &&  (itemInfo.extractEncryptionState ===  ExtractEncryptionState.pendingDecryption ||
                          itemInfo.extractEncryptionState ===  ExtractEncryptionState.pendingEncryption));
    const pendingCreation = itemInfo.extractCreationPending;
    const pending = pendingEncryption || pendingCreation;
    const lastRefreshedDate = itemInfo.lastRefreshedAt;
    const showExtractInfo = !pending && itemInfo.hasExtracts && lastRefreshedDate;
    let showEncryptionStatus = isEncryptionEnabled && itemInfo.hasEncryptableExtracts;
    if (showEncryptionStatus && itemInfo.contentType === FlowContentType.name && !FeatureFlags.maestroEarEnabled) {
      showEncryptionStatus = false;
    }
    const isExtractMoveToTheWebEnabled = itemInfo.contentType === WorkbookContentType.name ? isWebExtractionEnabled:
     extractMoveToTheWebEnabled(isWebExtractionEnabled, [itemInfo as IDataSourceDetailInfo]);
    return (
      <React.Fragment>
        {isExtractMoveToTheWebEnabled &&
          <span data-tb-test-id={SpaceHeaderTestIds.dataStatus} className={SpaceHeaderStyles.ExtractStatus}>
              {showDataStatus &&
                <div className={SpaceHeaderStyles.ExtractStatusLabel}>{Messages.extractStatusLabel()}</div>
              }
              { !pending &&
                  <ConnectedDataSourceActionDropdown itemInfo={itemInfo as ISpaceItemDetailInfo<'datasource'>} encryptionEnabled={isEncryptionEnabled}
                   siteEARMode={siteEARMode} disabled={!showDataStatus} availableActions = {availableActions}/>
                }
                { pending &&
                  <ConnectedCancelExtractActionDropdown itemInfo={itemInfo} hasExtracts={itemInfo.hasExtracts || itemInfo.hasEncryptableExtracts} encryptionEnabled={isEncryptionEnabled}
                  extractEncryptionState={extractEncryptionState(itemInfo)} extractCreationPending={itemInfo.extractCreationPending} disabled={false} siteEARMode={siteEARMode}
                  availableActions = {availableActions}/>
                }
                { showExtractInfo &&
                  <React.Fragment>
                    {!showEncryptionStatus &&
                      <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
                        {Messages.datasourceHeaderLastRefreshed()}
                      </span>
                    }
                    <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.lastRefreshed} title={Messages.datasourceHeaderLastRefreshed()}>
                      {L10n.localizedDateTime(lastRefreshedDate)}
                    </span>
                  </React.Fragment>
                }
            </span>
          }
      </React.Fragment>
    );
  }

export interface ExtractTaskMetadataOwnProps {
    itemInfo: ISpaceItemDetailInfo<'workbook' | 'datasource'>;
    availableActions: IWorkbookActions | IDatasourceActions;
  }

export interface ExtractTaskMetadataStateProps {
    isEncryptionEnabled: boolean;
    siteEARMode: ExtractEncryptionMode;
    isWebExtractionEnabled: boolean;
  }

export type ExtractTasksMetadataProps = ExtractTaskMetadataOwnProps & ExtractTaskMetadataStateProps;

const mapStateToProps: MapStateToProps<ExtractTaskMetadataStateProps> = (state: AppState) => {
    return {
      isEncryptionEnabled: SessionServerSelectors.extractEncryptionEnabledOmniSwitch(state),
      siteEARMode: SessionSiteSelectors.extractEncryptionMode(state),
      isWebExtractionEnabled: CompositeSessionInfoSelectors.isWebExtractionEnabled(state)
    };
  };

export const ConnectedExtractTasksMetadata = connect(mapStateToProps)(ExtractTasksMetadata);

function extractEncryptionState(itemInfo: ISpaceItemDetailInfo<'workbook' | 'datasource'>): ExtractEncryptionState {
    if (itemInfo.extractEncryptionState === ExtractEncryptionState.pendingDecryption || itemInfo.extractEncryptionState === ExtractEncryptionState.pendingEncryption)
    return itemInfo.extractEncryptionState;
    return ExtractEncryptionState.unencrypted;
  }
